/**
 * Notes hooks for user-created personal notes on lessons
 *
 * Provides hooks for:
 * - Listing notes for a lesson
 * - Creating, updating, and deleting notes
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { track } from '@/config/observability';
import {
  notesApi,
  type NoteItem,
  type NotesListResponse,
} from '@/lib/api-client';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const notesKeys = {
  all: ['notes'] as const,
  list: (lessonId: string) => [...notesKeys.all, 'list', lessonId] as const,
  detail: (noteId: string) => [...notesKeys.all, 'detail', noteId] as const,
  userAll: () => [...notesKeys.all, 'user-all'] as const,
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook for fetching all notes for a lesson.
 */
export function useNotesList(lessonId: string | undefined) {
  return useQuery<NotesListResponse>({
    queryKey: notesKeys.list(lessonId || ''),
    queryFn: () => notesApi.listNotes(lessonId!),
    enabled: !!lessonId,
    staleTime: 30_000,
  });
}

/**
 * Hook for creating a new note.
 */
export function useCreateNote(
  lessonId: string,
  origin: 'notes' | 'selection' | 'chat' = 'notes'
) {
  const queryClient = useQueryClient();

  return useMutation<
    NoteItem,
    Error,
    { title?: string; content?: string; section_id?: string }
  >({
    mutationFn: data => notesApi.createNote(lessonId, data),
    onSuccess: () => {
      track('note_created', { lesson_id: lessonId, origin });
      queryClient.invalidateQueries({ queryKey: notesKeys.list(lessonId) });
    },
  });
}

/**
 * Hook for updating a note (used by auto-save).
 */
export function useUpdateNote(lessonId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    NoteItem,
    Error,
    {
      noteId: string;
      data: { title?: string; content?: string; section_id?: string };
    }
  >({
    mutationFn: ({ noteId, data }) => notesApi.updateNote(noteId, data),
    onSuccess: updatedNote => {
      // Update the note in the list cache without refetching
      queryClient.setQueryData<NotesListResponse>(
        notesKeys.list(lessonId),
        old => {
          if (!old) return old;
          return {
            ...old,
            notes: old.notes.map(n =>
              n.note_id === updatedNote.note_id ? updatedNote : n
            ),
          };
        }
      );
    },
  });
}

/**
 * Hook for deleting a note.
 */
export function useDeleteNote(lessonId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: noteId => notesApi.deleteNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notesKeys.list(lessonId) });
    },
  });
}
